import { useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import {
  PerformanceGoalsMetricGroupPageDocument,
  PerformanceGoalsMetricGroupPageMetricsDocument,
} from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { InfiniteLoader, usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { F0Box } from '@factorialco/f0-react'
import { CreateMetricModal } from 'modules/goals/components/CreateMetricModal'
import { MetricDetailsPanel } from 'modules/goals/components/MetricDetailsPanel'
import { UpdateMetricGroupModal } from 'modules/goals/components/UpdateMetricGroupModal'
import { AssigneeCard } from 'modules/goals/revamp/components/AssigneeCard'
import { KeyResultsCard } from 'modules/goals/revamp/components/KeyResultsCard'
import { ProgressCard } from 'modules/goals/revamp/components/ProgressCard'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

const REFETCH_QUERIES = [
  PerformanceGoalsMetricGroupPageMetricsDocument,
  PerformanceGoalsMetricGroupPageDocument,
]

const GoalDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  if (!id) throw new Error('No id provided')

  const { data, loading, error } = useQuery(PerformanceGoalsMetricGroupPageDocument, {
    variables: { id },
  })

  const {
    nodes: metrics,
    loading: loadingMetrics,
    error: errorMetrics,
    data: metricsData,
    controls,
  } = usePaginatedQuery(PerformanceGoalsMetricGroupPageMetricsDocument, {
    variables: { id },
    extractConnection: ({ goals }) => goals?.metricsConnection,
  })

  const [searchParams, setSearchParams] = useSearchParams()
  const [showHistoryPanel, setShowHistoryPanel] = useState(false)

  const metricGroup = data?.goals?.metricGroup
  const summary = metricGroup?.summary
  const isGoalPublic = metricGroup?.public ?? false
  const canCreateGoalMetric = metricGroup?._policies?.createMetric.can
  const totalMetricsCount = metricsData?.goals?.metricsConnection?.totalCount ?? 0
  const singleMetric = totalMetricsCount === 1 && metrics.length === 1 ? metrics[0] : null
  const hasMultipleKRs = totalMetricsCount > 1
  const completedCount = metrics.filter(
    (m) => m.progressAssessmentStatus === 'achieved' || m.progressAssessmentStatus === 'over_achieved'
  ).length

  const employeesData = data?.goals?.employeesConnection
  const assigneesTotalCount = employeesData?.totalCount ?? 0
  const goalAssignees = employeesData?.edges?.map((edge) => edge?.node).filter(Boolean) ?? []

  const activeModal = searchParams.get('modal')

  useEffect(() => {
    if (loadingMetrics) return
    track(trackingEvents.goals.viewGoal, {
      number_key_results: totalMetricsCount,
    })
  }, [loadingMetrics, totalMetricsCount])

  return (
    <ResponsivePage.Body
      loading={loading || loadingMetrics}
      error={error || errorMetrics}
    >
      <F0Box display="grid" columns="1" gap="xl" alignItems="start" md={{ columns: "4" }}>
        <F0Box
          display="flex"
          flexDirection="column"
          gap="xl"
          md={{ colSpan: assigneesTotalCount > 0 ? "3" : "full" }}
        >
          {singleMetric ? (
            <ProgressCard
              type="single"
              metric={singleMetric}
              isGoalPublic={isGoalPublic}
              refetchQueries={REFETCH_QUERIES}
              onViewHistory={() => setShowHistoryPanel(true)}
            />
          ) : (
            <ProgressCard
              type="multi"
              progress={summary?.averageProgress ?? 0}
              needsAttention={summary?.needsAttention ?? false}
            />
          )}

          {hasMultipleKRs && (
            <InfiniteLoader controls={controls}>
              <KeyResultsCard
                metrics={metrics}
                totalCount={totalMetricsCount}
                completedCount={completedCount}
                refetchQueries={REFETCH_QUERIES}
                showAssignee={assigneesTotalCount !== 1}
                onAddKeyResult={canCreateGoalMetric ? () => setSearchParams({ modal: 'create' }) : undefined}
              />
            </InfiniteLoader>
          )}
        </F0Box>

        {assigneesTotalCount > 0 && goalAssignees.length > 0 && (
          <AssigneeCard assignees={goalAssignees} totalCount={assigneesTotalCount} />
        )}
      </F0Box>

      {activeModal === 'create' && (
        <CreateMetricModal
          showEmployeeSelection
          onClose={() => setSearchParams({})}
          refetchQueries={[PerformanceGoalsMetricGroupPageMetricsDocument]}
          defaultValues={{ metricGroupId: String(id) }}
        />
      )}

      {activeModal === 'edit' && metricGroup && (
        <UpdateMetricGroupModal metricGroup={metricGroup} onClose={() => setSearchParams({})} />
      )}

      {showHistoryPanel && singleMetric && (
        <MetricDetailsPanel metricId={singleMetric.id} onClose={() => setShowHistoryPanel(false)} />
      )}
    </ResponsivePage.Body>
  )
}

export default GoalDetailPage
